import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { State } from '@store/index';
import * as AppSelectors from '@store/selectors/app.selectors';
import { EMPTY, of } from 'rxjs';
import { catchError, concatMap, delay, map, withLatestFrom } from 'rxjs/operators';
import { SourcesApiService } from 'src/app/apis/sources-api.service';
import { TagsApiService } from 'src/app/apis/tags-api.service';
import { CapacitorStorageService } from 'src/app/lib/capacitor-storage.service';
import { LocalStorageService } from 'src/app/lib/local-storage.service';
import { UtilsService } from 'src/app/lib/utils.service';
import * as AppActions from '../actions/app.actions';
import { Location } from '@angular/common';

@Injectable()
export class AppEffects {

  loadSources$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(AppActions.loadSources),
      concatMap(() => {

        if (!this.localStorageService.isAuthenticated()) {
          this.router.navigate(["/auth/login"]);
          return EMPTY;
        }

        const restaurantId = this.localStorageService.getRestaurantId();

        if (restaurantId == null) {
          console.error('Restaurant Id does not exist!'); // todo: maybe redirect to the login page
          return EMPTY;
        }

        return this.sourcesApiService.getAllSources(parseInt(restaurantId)).pipe(
          map(data => AppActions.loadSourcesSuccess({ sources: data.sources.sort((a, b) => (a.companyTitle ?? '') > (b.companyTitle ?? '') ? 1 : -1) })),
          catchError(error => of(AppActions.loadSourcesFailure({ error }))))
      }
      )
    );
  });

  loadTags$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(AppActions.loadTags),
      concatMap(() => {

        if (!this.localStorageService.isAuthenticated()) {
          this.router.navigate(["/auth/login"]);
          return EMPTY;
        }

        if (!this.utilsService.canRead('Tags') && !this.utilsService.canWrite('ManageTags')) {
          return EMPTY;
        }

        const restaurantId = this.localStorageService.getRestaurantId();

        if (restaurantId == null) {
          console.error('Restaurant Id does not exist!'); // todo: maybe redirect to the login page
          return EMPTY;
        }

        return this.tagsApiService.getAllCategories(parseInt(restaurantId)).pipe(
          map(data => AppActions.loadTagsSuccess({ tags: data })),
          catchError(error => of(AppActions.loadTagsFailure({ error }))))
      }
      )
    );
  });

  loadAppSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppActions.loadAppSettings),
      concatMap(async () => {
        const appSettings = await this.capacitorStorageService.getAppSettings();
        return AppActions.setAppSettings({ appSettings });
      })
    );
  });

  saveEditingAppSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppActions.saveEditingAppSettings),
      withLatestFrom(
        this.store.select(AppSelectors.selectEditingAppSettings),
      ),
      concatMap(async ([action, appSettings]) => {
        this.capacitorStorageService.setAppSettings(appSettings);
        this.goBack();
        return AppActions.setAppSettings({ appSettings });
      })
    );
  });

  showToast$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppActions.showToast),
      delay(1000),
      concatMap(async () => {
        return AppActions.showToast({ message: null });
      })
    );
  });

  goBack() {
    this.location.back();
  }

  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private localStorageService: LocalStorageService,
    private sourcesApiService: SourcesApiService,
    private router: Router,
    private tagsApiService: TagsApiService,
    private utilsService: UtilsService,
    private location: Location,
    private capacitorStorageService: CapacitorStorageService
  ) { }

}
